import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

// Europe only: bank card or IBAN/SEPA transfer (EUR)
const METHODS = {
  card_eu: { fields: ["cardNumber", "fullName"] },
  sepa_eu: { fields: ["iban", "swift", "bankName", "fullName"] },
};

function parseDetails(method, raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch (_) { /* legacy free text */ }
  return { _legacy: raw };
}

function maskValue(method, fields) {
  if (!fields) return "";
  if (method === "card_eu" && fields.cardNumber) {
    const last4 = String(fields.cardNumber).replace(/\D/g, "").slice(-4);
    return `**** ${last4}${fields.fullName ? ` • ${fields.fullName}` : ""}`;
  }
  if (method === "sepa_eu" && fields.iban) {
    const iban = String(fields.iban).replace(/\s/g, "");
    return `IBAN ${iban.slice(0, 4)}…${iban.slice(-4)}`;
  }
  if (fields._legacy) return fields._legacy.slice(0, 60);
  return "";
}

export default function PayoutDetailsForm({ API, partner, onSaved }) {
  const { t } = useTranslation();
  const initialMethod = partner?.payout_method || "";
  const initialFields = useMemo(
    () => parseDetails(initialMethod, partner?.payout_details),
    [initialMethod, partner?.payout_details]
  );

  const [editing, setEditing] = useState(!initialMethod);
  const [method, setMethod] = useState(METHODS[initialMethod] ? initialMethod : "");
  const [fields, setFields] = useState(initialFields);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditing(!partner?.payout_method);
    setMethod(METHODS[partner?.payout_method] ? partner.payout_method : "");
    setFields(parseDetails(partner?.payout_method, partner?.payout_details));
  }, [partner?.payout_method, partner?.payout_details]);

  const handleMethodChange = (e) => {
    setMethod(e.target.value);
    setFields({});
  };

  const handleFieldChange = (fieldKey, value) => {
    setFields((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSave = async () => {
    if (!method) {
      toast.error(t("partner.dash.payout.fillFirst"));
      return;
    }
    const required = METHODS[method]?.fields || [];
    const missing = required.find((f) => !String(fields[f] || "").trim());
    if (missing) {
      toast.error(t("partner.dash.payout.fillFirst"));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/partner/payout-details`, {
        payout_method: method,
        payout_details: JSON.stringify(fields),
      });
      toast.success(t("partner.dash.payout.saved"));
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(t("partner.dash.payout.saveError"));
    } finally {
      setSaving(false);
    }
  };

  // Collapsed read-only summary
  if (!editing && partner?.payout_method) {
    const summary = maskValue(partner.payout_method, parseDetails(partner.payout_method, partner.payout_details));
    return (
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold mb-1">{t("partner.dash.payout.title")}</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider bg-green-500/20 text-green-400 px-2 py-0.5 rounded font-bold">
                {t("partner.dash.payout.savedSummary")}
              </span>
              <span className="text-sm text-zinc-300">
                {t(`partner.dash.payout.method.${partner.payout_method}`) || partner.payout_method}
              </span>
            </div>
            <div className="text-sm font-mono text-zinc-500 break-all">{summary}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            className="border-zinc-700 text-white hover:bg-zinc-900 shrink-0"
            data-testid="payout-edit-btn"
          >
            <Pencil className="w-3 h-3 mr-1" />
            {t("partner.dash.payout.edit")}
          </Button>
        </div>
      </div>
    );
  }

  // Expanded edit form
  const cur = METHODS[method];
  const selectCls = "w-full bg-zinc-900 border border-zinc-800 rounded-md text-white mt-1 mb-3 px-3 py-2 text-sm focus:outline-none focus:border-yellow-300 appearance-none cursor-pointer";

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
      <h3 className="text-lg font-semibold mb-1">{t("partner.dash.payout.title")}</h3>
      <p className="text-sm text-zinc-500 mb-5">{t("partner.dash.payout.subtitleEu")}</p>

      <label className="text-xs uppercase tracking-wider text-zinc-500">
        {t("partner.dash.payout.method")}
      </label>
      <select value={method} onChange={handleMethodChange} className={selectCls} data-testid="payout-method-select">
        <option value="">{t("partner.dash.payout.methodPlaceholder")}</option>
        {Object.keys(METHODS).map((m) => (
          <option key={m} value={m}>
            {t(`partner.dash.payout.method.${m}`)}
          </option>
        ))}
      </select>

      {cur && (
        <div className="border-t border-zinc-800 pt-4 mt-2">
          {cur.fields.map((f) => {
            const isCard = f === "cardNumber";
            return (
              <div key={f}>
                <label className="text-xs uppercase tracking-wider text-zinc-500">
                  {t(`partner.dash.payout.field.${f}`)}
                </label>
                <Input
                  value={fields[f] || ""}
                  onChange={(e) => handleFieldChange(f, isCard
                    ? e.target.value.replace(/[^\d ]/g, "").slice(0, 23)
                    : e.target.value
                  )}
                  placeholder={isCard ? "0000 0000 0000 0000" : f === "iban" ? "FR76 3000 6000 0112 3456 7890 189" : ""}
                  className="bg-zinc-900 border-zinc-800 text-white mt-1 mb-3"
                  data-testid={`payout-field-${f}`}
                />
              </div>
            );
          })}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-yellow-300 text-black hover:bg-yellow-200 font-semibold"
              data-testid="payout-save-details-btn"
            >
              {saving ? "..." : t("partner.dash.payout.save")}
            </Button>
            {partner?.payout_method && (
              <Button
                variant="outline"
                onClick={() => setEditing(false)}
                className="border-zinc-700 text-white hover:bg-zinc-900"
                data-testid="payout-cancel-edit-btn"
              >
                {t("admin.cancel") || "Отмена"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
